import React, { createContext, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import {
  fetchManagerById,
  fetchTrainerById,
  fetchMembersByIds,
  getGymMembers,
  getGymGroups,
  getGymTrainers,
  fetchGroupsByIds,
  getGymManagers,
  getGymProducts,
  fetchGym,
  fetchAllTrainers,
  fetchAllManagers,
  sendVerificationCode,
  verifyLoginCode,
  VerificationCodeResponse,
} from '../api';
import {
  setSelectedGymId,
  setLoginManagerId,
  setLoginTrainerId,
  setLoginMemberId,
  setGymDashboardData,
  setExercise,
  setRemovedExercise,
  setRemovedGroup,
  setRemovedProgram,
  setRemovedTemplate,
  setGroup,
  setManagers,
  setTrainers,
  setMembers,
  setRemovedMember,
  setRemovedManager,
  setRemovedTrainer,
  setGym,
  setProgram,
  setTemplate,
  setGymProducts,
  resetAllState,
} from '../appState/stateActions';
import { useErrorContext } from './errorProvider';
import { useGlobalAction } from './globalActionProvider';
import {
  clearLoginStorageData,
  setLoginStorageData,
} from '../api/loginApiHelper';

import type {
  AppState,
} from '../appState/appInitialState';
import type {
  Gym,
  Exercise,
  TrainingRoutineTemplate,
  Member,
  Trainer,
  Manager,
  Program,
  Group,
} from '../types';

const getUniqueGymIds = (
  manager?: Manager | null,
  trainer?: Trainer | null,
  member?: Member | null,
): string[] => {
  return [
    ...new Set([
      ...(manager?.gymIds || []),
      ...(member?.gymIds || []),
      ...(trainer?.gymIds || []),
    ]),
  ];
};

export interface SessionActionContextProps {
  logInUser: (
    managerId?: string,
    trainerId?: string,
    memberId?: string,
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  isImpersonating: boolean;
  refreshExercise: (exercise: Exercise) => void;
  refreshExercisesAfterDelete: (exerciseId: string, trainerId: string) => void;
  refreshGroupsAfterDelete: (groupId: string, trainerId: string) => void;
  refreshProgramsAfterDelete: (programId: string, trainerId: string) => void;
  refreshTemplatesAfterDelete: (templateId: string, trainerId: string) => void;
  refreshGroup: (group: Group) => void;
  refreshMembers: (memberIds: string[]) => Promise<boolean>;
  removeMemberAssociations: (memberId: string, trainerIds: string[]) => void;
  refreshGym: (gym: Gym) => void;
  refreshManager: (manager: Manager) => void;
  removeManager: (managerId: string) => void;
  refreshTrainer: (trainer: Trainer) => void;
  refreshMember: (member: Member) => void;
  refreshProgram: (program: Program) => void;
  refreshTemplate: (template: TrainingRoutineTemplate) => void;
  refreshGymProducts: (gymId: string) => Promise<boolean>;
  removeTrainer: (trainerId: string) => void;
  sendLoginVerificationCode: (email: string) => Promise<boolean>;
  getLoginCodeVerification: (
    email: string,
    code: string,
  ) => Promise<VerificationCodeResponse | null>;
  lookupAccountByEmail: (
    email: string,
  ) => Promise<VerificationCodeResponse | null>;
  impersonateAccount: (account: VerificationCodeResponse) => Promise<boolean>;
  ensureExerciseLibraryLoaded: (trainerId?: string) => Promise<void>;
  ensureGymManagersLoaded: (gymId: string) => Promise<void>;
  hydrateLiveWorkoutsDataForGym: (gymId: string) => Promise<void>;
}

const SessionActionContext = createContext<SessionActionContextProps>({
  logInUser: () => Promise.resolve(false),
  logout: () => Promise.resolve(),
  isImpersonating: false,
  refreshExercise: () => {},
  refreshExercisesAfterDelete: () => {},
  refreshGroupsAfterDelete: () => {},
  refreshProgramsAfterDelete: () => {},
  refreshTemplatesAfterDelete: () => {},
  refreshGroup: () => {},
  refreshMembers: () => Promise.resolve(false),
  removeMemberAssociations: () => {},
  refreshGym: () => {},
  refreshManager: () => {},
  removeManager: () => {},
  refreshTrainer: () => {},
  refreshMember: () => {},
  refreshProgram: () => {},
  refreshTemplate: () => {},
  refreshGymProducts: () => Promise.resolve(false),
  removeTrainer: () => {},
  sendLoginVerificationCode: () => Promise.resolve(false),
  getLoginCodeVerification: () => Promise.resolve(null),
  lookupAccountByEmail: () => Promise.resolve(null),
  impersonateAccount: () => Promise.resolve(false),
  ensureExerciseLibraryLoaded: () => Promise.resolve(),
  ensureGymManagersLoaded: () => Promise.resolve(),
  hydrateLiveWorkoutsDataForGym: () => Promise.resolve(),
} as SessionActionContextProps);

export const SessionActionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { setGlobalError } = useErrorContext();
  const { loadSessionExercises, loadSessionMuscles } = useGlobalAction();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const loginTrainerId = useSelector((state: AppState) => state.loginTrainerId);
  const hasLoadedExerciseLibraryRef = useRef(false);
  const exerciseLibraryLoadPromiseRef = useRef<Promise<void> | null>(null);
  const hydratedGymManagersRef = useRef<Set<string>>(new Set());
  const hydratedLiveWorkoutsGymRef = useRef<Set<string>>(new Set());
  const hydratingLiveWorkoutsGymRef = useRef<Set<string>>(new Set());

  const logInUser = async (
    managerId?: string,
    trainerId?: string,
    memberId?: string,
  ): Promise<boolean> => {
    if (managerId) {
      dispatch(setLoginManagerId(managerId));
    }
    if (trainerId) {
      dispatch(setLoginTrainerId(trainerId));
    }
    if (memberId) {
      dispatch(setLoginMemberId(memberId));
    }

    const [managerResult, trainerResult] = await Promise.all([
      managerId ? fetchManagerById(managerId) : Promise.resolve(null),
      trainerId ? fetchTrainerById(trainerId) : Promise.resolve(null),
    ]);

    if (managerId && (!managerResult || !managerResult.success)) {
      setGlobalError(
        managerResult?.error?.message || 'Unable to load manager profile',
      );
      return false;
    }

    if (trainerId && (!trainerResult || !trainerResult.success)) {
      setGlobalError(
        trainerResult?.error?.message || 'Unable to load trainer profile',
      );
      return false;
    }

    const managerData = managerResult?.data ?? null;
    const trainerData = trainerResult?.data ?? null;

    if (trainerId && !trainerData) {
      setGlobalError('Trainer profile not found');
      return false;
    }

    if (managerId && !managerData) {
      setGlobalError('Manager profile not found');
      return false;
    }

    const allGymIds = getUniqueGymIds(managerData, trainerData);

    if (allGymIds.length === 0) {
      setGlobalError('No gyms found');
      return false;
    }

    dispatch(setSelectedGymId(allGymIds[0]));

    await Promise.all(
      allGymIds.map(async (gymId) => {
        const [{ data: gym }, { data: trainers }, { data: gymProducts }] =
          await Promise.all([
            fetchGym(gymId),
            getGymTrainers(gymId),
            getGymProducts(gymId),
          ]);

        if (!gym) {
          setGlobalError('Error fetching gym');
          return;
        }

        const managersForGym = (
          managerData?.id &&
          managerData?.gymIds?.includes(gymId) &&
          managerData?.isActive !== false
            ? [managerData]
            : []
        ) as Manager[];
        const shouldIncludeLoginManager =
          Boolean(managerData?.id) &&
          Boolean(managerData?.gymIds?.includes(gymId)) &&
          managerData?.isActive !== false &&
          !managersForGym.some(({ id }) => id === managerData?.id);
        const activeManagers = shouldIncludeLoginManager
          ? [...managersForGym, managerData as Manager]
          : managersForGym;
        const activeTrainers = (trainers ?? []).filter(
          ({ isActive }) => isActive === true,
        );
        const hydratedActiveTrainers = activeTrainers.map((trainer) =>
          trainerData?.id === trainer.id
            ? { ...trainer, ...trainerData }
            : trainer,
        );

        const trainersDashboardData = hydratedActiveTrainers.map((trainer) => ({
          trainer,
          members: {},
          groups: {},
          personalizedExercises: {},
          templates: {},
          programs: {},
        }));

        dispatch(
          setGymDashboardData({
            gym,
            managers: _.keyBy(activeManagers, 'id'),
            trainers: _.keyBy(trainersDashboardData, 'trainer.id'),
            unassignedMembers: {},
            gymProducts: gymProducts ?? [],
          }),
        );
      }),
    );

    // Call the muscles and exercises APIs immediately after initial load so they are ready for immediate use.
    void ensureExerciseLibraryLoaded(trainerId || undefined);

    return Boolean(managerData || trainerData);
  };

  const ensureExerciseLibraryLoaded = async (
    trainerId?: string,
  ): Promise<void> => {
    if (hasLoadedExerciseLibraryRef.current) {
      return;
    }
    if (exerciseLibraryLoadPromiseRef.current) {
      return exerciseLibraryLoadPromiseRef.current;
    }

    const effectiveTrainerId = trainerId || loginTrainerId || undefined;
    const loadPromise = Promise.all([
      loadSessionMuscles(),
      loadSessionExercises(effectiveTrainerId),
    ])
      .then(() => {
        hasLoadedExerciseLibraryRef.current = true;
      })
      .finally(() => {
        exerciseLibraryLoadPromiseRef.current = null;
      });

    exerciseLibraryLoadPromiseRef.current = loadPromise;
    return loadPromise;
  };

  const ensureGymManagersLoaded = async (gymId: string): Promise<void> => {
    if (!gymId || hydratedGymManagersRef.current.has(gymId)) {
      return;
    }
    const { data, success, error } = await getGymManagers(gymId);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error fetching gym managers');
      return;
    }
    dispatch(setManagers(data));
    hydratedGymManagersRef.current.add(gymId);
  };

  const hydrateLiveWorkoutsDataForGym = async (gymId: string): Promise<void> => {
  if (
    !gymId ||
    hydratedLiveWorkoutsGymRef.current.has(gymId) ||
    hydratingLiveWorkoutsGymRef.current.has(gymId)
  ) {
    return;
  }
  hydratingLiveWorkoutsGymRef.current.add(gymId);

  try {
    const [
      { data: gymMembers, success: membersSuccess, error: membersError },
      { data: gymGroupSummaries, success: groupsSuccess, error: groupsError },
    ] = await Promise.all([getGymMembers(gymId), getGymGroups(gymId)]);

    if (!membersSuccess || !gymMembers) {
      setGlobalError(membersError?.message || 'Error fetching live workout members');
      return;
    }
    if (!groupsSuccess || !gymGroupSummaries) {
      setGlobalError(groupsError?.message || 'Error fetching live workout groups');
      return;
    }

    const groupIds = gymGroupSummaries.map((g) => g.id).filter(Boolean);

    const { data: fullGroups } = groupIds.length
      ? await fetchGroupsByIds(groupIds)
      : { data: [] };

    dispatch(setMembers(gymMembers.filter(({ isActive }) => isActive)));
    (fullGroups ?? []).forEach((group) => dispatch(setGroup(group)));

    hydratedLiveWorkoutsGymRef.current.add(gymId);
  } finally {
    hydratingLiveWorkoutsGymRef.current.delete(gymId);
  }
};

  const refreshExercise = (exercise: Exercise): void => {
    dispatch(setExercise(exercise));
  };

  const refreshExercisesAfterDelete = (
    exerciseId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedExercise(exerciseId, trainerId));
  };

  const refreshGroupsAfterDelete = (
    groupId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedGroup(groupId, trainerId));
  };

  const refreshProgramsAfterDelete = (
    programId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedProgram(programId, trainerId));
  };

  const refreshTemplatesAfterDelete = (
    templateId: string,
    trainerId: string,
  ): void => {
    dispatch(setRemovedTemplate(templateId, trainerId));
  };

  const refreshGroup = (group: Group): void => {
    dispatch(setGroup(group));
  };

  const refreshMembers = async (memberIds: string[]): Promise<boolean> => {
    if (!memberIds?.length) {
      return true;
    }
    const {
      data: members,
      success,
      error,
    } = await fetchMembersByIds(memberIds);
    if (!success || !members) {
      setGlobalError(error?.message || 'Error refreshing members');
      return false;
    }
    dispatch(setMembers(members));
    return true;
  };

  const removeMemberAssociations = (
    memberId: string,
    trainerIds: string[],
  ): void => {
    if (!trainerIds?.length) {
      return;
    }
    dispatch(setRemovedMember(memberId, trainerIds));
  };

  const refreshGym = (gym: Gym): void => {
    dispatch(setGym(gym));
  };

  const refreshManager = (manager: Manager): void => {
    dispatch(setManagers([manager]));
  };

  const removeManager = (managerId: string): void => {
    if (!managerId) {
      return;
    }
    dispatch(setRemovedManager(managerId));
  };

  const refreshTrainer = (trainer: Trainer): void => {
    dispatch(setTrainers([trainer]));
  };

  const removeTrainer = (trainerId: string): void => {
    if (!trainerId) {
      return;
    }
    dispatch(setRemovedTrainer(trainerId));
  };

  const refreshMember = (member: Member): void => {
    dispatch(setMembers([_.cloneDeep(member)]));
  };

  const refreshProgram = (program: Program): void => {
    dispatch(setProgram(_.cloneDeep(program)));
  };

  const refreshTemplate = (template: TrainingRoutineTemplate): void => {
    dispatch(setTemplate(_.cloneDeep(template)));
  };

  const refreshGymProducts = async (gymId: string): Promise<boolean> => {
    if (!gymId) {
      setGlobalError('Gym id is required to refresh products');
      return false;
    }
    const { data, success, error } = await getGymProducts(gymId);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error refreshing gym products');
      return false;
    }
    dispatch(setGymProducts(gymId, data));
    return true;
  };

  const sendLoginVerificationCode = async (email: string): Promise<boolean> => {
    const { success, error } = await sendVerificationCode(email);
    if (!success) {
      setGlobalError(error?.message || 'Error sending verification code');
    }
    return success;
  };

  const getLoginCodeVerification = async (
    email: string,
    code: string,
  ): Promise<VerificationCodeResponse | null> => {
    const { data, success, error } = await verifyLoginCode(email, code);
    if (!success) {
      setGlobalError(error?.message || 'Error verifying login code');
    }
    return data;
  };

  const lookupAccountByEmail = async (
    email: string,
  ): Promise<VerificationCodeResponse | null> => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) {
      setGlobalError('Email is required');
      return null;
    }
    // NOTE: No tokens here — impersonation bypasses real auth.
    // hasValidLoginStorageData will return false for this session.
    // Impersonation is a dev-only tool and this is an accepted limitation.
    const [trainersResponse, managersResponse] = await Promise.all([
      fetchAllTrainers(),
      fetchAllManagers(),
    ]);
    if (!trainersResponse.success || !trainersResponse.data) {
      setGlobalError(
        trainersResponse.error?.message ||
          'Error retrieving trainers for impersonation',
      );
      return null;
    }
    const trainer = trainersResponse.data.find(
      (candidate) => candidate.email?.toLowerCase() === normalizedEmail,
    );
    if (!trainer) {
      return null;
    }

    let managerId: string | undefined;
    if (managersResponse.success && managersResponse.data) {
      const manager = managersResponse.data.find(
        (candidate) => candidate.email?.toLowerCase() === normalizedEmail,
      );
      managerId = manager?.id;
    } else if (!managersResponse.success) {
      setGlobalError(
        managersResponse.error?.message ||
          'Error retrieving managers for impersonation',
      );
    }

    return {
      success: true,
      trainerId: trainer.id,
      managerId,
      memberId: undefined,
    };
  };

  const impersonateAccount = async (
    account: VerificationCodeResponse,
  ): Promise<boolean> => {
    if (!account?.trainerId) {
      setGlobalError('Trainer ID is required for impersonation');
      return false;
    }

    setIsImpersonating(true);
    try {
      dispatch(resetAllState());
      clearLoginStorageData();
      hasLoadedExerciseLibraryRef.current = false;
      exerciseLibraryLoadPromiseRef.current = null;
      hydratedLiveWorkoutsGymRef.current.clear();
      hydratingLiveWorkoutsGymRef.current.clear();

      const loginStorageData = {
        isImpersonating: true, // ADD
        accessToken: undefined, // no tokens for impersonation
        refreshToken: undefined,
        managerId: account.managerId,
        trainerId: account.trainerId,
        memberId: account.memberId,
      };

      setLoginStorageData(loginStorageData);
      const success = await logInUser(
        loginStorageData.managerId,
        loginStorageData.trainerId,
        loginStorageData.memberId,
      );

      if (!success) {
        clearLoginStorageData();
        setGlobalError('Unable to impersonate selected account');
      }

      return success;
    } finally {
      setIsImpersonating(false);
    }
  };

  const logout = async (): Promise<void> => {
    clearLoginStorageData();
    dispatch(resetAllState());
    hasLoadedExerciseLibraryRef.current = false;
    exerciseLibraryLoadPromiseRef.current = null;
    hydratedLiveWorkoutsGymRef.current.clear();
    hydratingLiveWorkoutsGymRef.current.clear();
  };

  return (
    <SessionActionContext.Provider
      value={{
        logInUser,
        logout,
        isImpersonating,
        refreshExercise,
        refreshExercisesAfterDelete,
        refreshGroupsAfterDelete,
        refreshProgramsAfterDelete,
        refreshTemplatesAfterDelete,
        refreshGroup,
        refreshMembers,
        removeMemberAssociations,
        refreshGym,
        refreshManager,
        removeManager,
        refreshTrainer,
        refreshMember,
        refreshProgram,
        refreshTemplate,
        refreshGymProducts,
        removeTrainer,
        sendLoginVerificationCode,
        getLoginCodeVerification,
        lookupAccountByEmail,
        impersonateAccount,
        ensureExerciseLibraryLoaded,
        ensureGymManagersLoaded,
        hydrateLiveWorkoutsDataForGym,
      }}
    >
      {children}
    </SessionActionContext.Provider>
  );
};

export const useSessionAction = () => {
  return useContext(SessionActionContext);
};
